import React, { useRef, useState } from 'react';
import {
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Button,
  Textarea,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Code,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import {
  FiUpload,
  FiDownload,
  FiFileText,
  FiCheckCircle,
  FiInfo
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import {
  parseMasterDataFile,
  parseMasterDataJson,
  summarizeMasterData
} from '../../utils/masterDataImport';
import api from '../../services/api';

const MasterDataImportExport = ({ onDataChange }) => {
  const fileInputRef = useRef(null);
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [parsingFile, setParsingFile] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [importSummary, setImportSummary] = useState(null);

  const sampleData = {
    categories: [
      {
        code: "HVAC",
        name: "HVAC Systems",
        description: "Heating, Ventilation, and Air Conditioning",
        order: 1,
        activities: [
          {
            code: "HVAC-HEX",
            name: "Heat Exchanger Installation",
            description: "Heat exchanger equipment installation",
            defaultUnit: "No",
            order: 1,
            subTasks: [
              {
                name: "Heat Exchanger Erection (up to 350 TR)",
                description: "Installation and setup of heat exchanger units",
                defaultProductivity: 64.0,
                unit: "No",
                order: 1
              },
              {
                name: "Valve Package Installation",
                description: "Installation of valve packages for heat exchanger",
                defaultProductivity: 252.0,
                unit: "Item",
                order: 2
              }
            ]
          }
        ]
      },
      {
        code: "PL",
        name: "Plumbing",
        description: "Water supply and drainage systems",
        order: 2,
        activities: [
          {
            code: "PL-PIPE",
            name: "Pipe Installation",
            description: "Various pipe installation works",
            defaultUnit: "m",
            order: 1,
            subTasks: [
              {
                name: "UPVC Pipe Installation (50-110mm)",
                description: "Above ground UPVC pipe installation",
                defaultProductivity: 0.45,
                unit: "m",
                order: 1
              }
            ]
          }
        ]
      }
    ]
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error('Please provide import data');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      let parsedData;
      try {
        parsedData = parseMasterDataJson(importData);
      } catch (parseError) {
        toast.error(parseError.message);
        return;
      }

      const response = await api.post('/super-admin/master/import', parsedData);
      const result = response.data;

      if (result.success) {
        setImportResult(result.data);
        toast.success(result.message);
        setImportData('');
        setSelectedFileName('');
        setImportSummary(null);
        onDataChange?.();
      } else {
        toast.error(result.error || result.message || 'Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Import failed. Please try again.';
      if (!error.response?.data?.message) {
        toast.error(errorMessage);
      }
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setParsingFile(true);
    setImportResult(null);

    try {
      const parsedData = await parseMasterDataFile(file);
      const summary = summarizeMasterData(parsedData);
      setImportData(JSON.stringify(parsedData, null, 2));
      setSelectedFileName(file.name);
      setImportSummary(summary);
      toast.success(
        `Prepared ${summary.categories} categories, ${summary.activities} activities, and ${summary.subTasks} sub-tasks.`
      );
    } catch (error) {
      console.error('File parsing error:', error);
      setSelectedFileName('');
      setImportSummary(null);
      toast.error(error.message || 'Could not read the selected file.');
    } finally {
      setParsingFile(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/super-admin/master/export', {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `master-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Master data exported successfully');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const loadSampleData = () => {
    setImportData(JSON.stringify(sampleData, null, 2));
    setSelectedFileName('Sample data');
    setImportSummary(summarizeMasterData(sampleData));
    setImportResult(null);
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Card bg="gray.800" borderColor="gray.700">
        <CardHeader>
          <VStack align="start" spacing={1}>
            <Heading size="md" color="white">
              Import & Export Master Data
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Bulk operations for managing construction categories, activities, and sub-tasks
            </Text>
          </VStack>
        </CardHeader>
      </Card>

      <HStack spacing={6} align="start">
        {/* Import Section */}
        <VStack spacing={4} align="stretch" flex="1">
          <Card bg="gray.800" borderColor="gray.700">
            <CardHeader>
              <HStack spacing={2}>
                <FiUpload />
                <Heading size="sm" color="white">Import Data</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm">
                      Choose an Excel or JSON file, or paste JSON below. Excel columns are read as
                      Category → Activity → Sub-task with unit and productivity/man-hours.
                    </Text>
                  </Box>
                </Alert>

                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.json"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <Button
                    leftIcon={<FiFileText />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    isLoading={parsingFile}
                    loadingText="Reading file"
                  >
                    Choose Excel or JSON
                  </Button>
                </Box>

                <Box bg="gray.700" borderRadius="md" p={4}>
                  <Text color="white" fontSize="sm" fontWeight="semibold" mb={1}>
                    Excel import templates
                  </Text>
                  <Text color="gray.300" fontSize="xs" mb={3}>
                    Use the blank file to enter your data, or download the sample to see how
                    categories, activities, and sub-tasks must be repeated across rows.
                  </Text>
                  <HStack spacing={3} flexWrap="wrap">
                    <Button
                      as="a"
                      href="/templates/master-data-import-template.xlsx"
                      download="master-data-import-template.xlsx"
                      leftIcon={<FiDownload />}
                      colorScheme="blue"
                      variant="outline"
                      size="sm"
                    >
                      Blank Excel Template
                    </Button>
                    <Button
                      as="a"
                      href="/templates/master-data-import-sample.xlsx"
                      download="master-data-import-sample.xlsx"
                      leftIcon={<FiFileText />}
                      colorScheme="teal"
                      variant="outline"
                      size="sm"
                    >
                      Sample Excel Template
                    </Button>
                  </HStack>
                </Box>

                {importSummary && (
                  <Alert status="success" variant="subtle">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" mb={1}>
                        {selectedFileName} is ready
                      </Text>
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge colorScheme="blue">
                          {importSummary.categories} categories
                        </Badge>
                        <Badge colorScheme="green">
                          {importSummary.activities} activities
                        </Badge>
                        <Badge colorScheme="orange">
                          {importSummary.subTasks} sub-tasks
                        </Badge>
                      </HStack>
                    </Box>
                  </Alert>
                )}

                <FormControl>
                  <FormLabel color="gray.300">Import preview (JSON)</FormLabel>
                  <Textarea
                    value={importData}
                    onChange={(event) => {
                      setImportData(event.target.value);
                      setSelectedFileName('');
                      setImportSummary(null);
                      setImportResult(null);
                    }}
                    placeholder="Paste your JSON data here..."
                    rows={15}
                    bg="gray.700"
                    color="white"
                    borderColor="gray.600"
                    fontFamily="mono"
                    fontSize="sm"
                  />
                </FormControl>

                <HStack>
                  <Button
                    leftIcon={<FiUpload />}
                    colorScheme="blue"
                    onClick={handleImport}
                    isLoading={importing}
                    isDisabled={parsingFile || !importData.trim()}
                  >
                    Import Data
                  </Button>
                  <Button
                    leftIcon={<FiFileText />}
                    variant="outline"
                    onClick={loadSampleData}
                  >
                    Load Sample
                  </Button>
                </HStack>

                {importResult && (
                  <Alert status="success">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold" mb={2}>Import Completed!</Text>
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <Badge colorScheme="blue">{importResult.totalImported.categories}</Badge>
                          <Text fontSize="sm">Categories created/updated</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="green">{importResult.totalImported.activities}</Badge>
                          <Text fontSize="sm">Activities created/updated</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="orange">{importResult.totalImported.subTasks}</Badge>
                          <Text fontSize="sm">Sub-tasks created/updated</Text>
                        </HStack>
                      </VStack>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>

        {/* Export Section */}
        <VStack spacing={4} align="stretch" flex="1">
          <Card bg="gray.800" borderColor="gray.700">
            <CardHeader>
              <HStack spacing={2}>
                <FiDownload />
                <Heading size="sm" color="white">Export Data</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm">
                      Export all master data including categories, activities, and sub-tasks 
                      in JSON format.
                    </Text>
                  </Box>
                </Alert>

                <Button
                  leftIcon={<FiDownload />}
                  colorScheme="green"
                  onClick={handleExport}
                  isLoading={exporting}
                  size="lg"
                >
                  Export Master Data
                </Button>

                <Box>
                  <Text color="gray.300" fontSize="sm" fontWeight="bold" mb={2}>
                    Export includes:
                  </Text>
                  <List spacing={1}>
                    <ListItem color="gray.400" fontSize="sm">
                      <ListIcon as={FiCheckCircle} color="green.400" />
                      All master categories with details
                    </ListItem>
                    <ListItem color="gray.400" fontSize="sm">
                      <ListIcon as={FiCheckCircle} color="green.400" />
                      All activities within each category
                    </ListItem>
                    <ListItem color="gray.400" fontSize="sm">
                      <ListIcon as={FiCheckCircle} color="green.400" />
                      All sub-tasks with productivity rates
                    </ListItem>
                    <ListItem color="gray.400" fontSize="sm">
                      <ListIcon as={FiCheckCircle} color="green.400" />
                      Hierarchical structure maintained
                    </ListItem>
                  </List>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* JSON Format Guide */}
          <Card bg="gray.800" borderColor="gray.700">
            <CardHeader>
              <HStack spacing={2}>
                <FiInfo />
                <Heading size="sm" color="white">JSON Format Guide</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                <Text color="gray.300" fontSize="sm">
                  Required JSON structure:
                </Text>
                <Code colorScheme="blue" p={3} borderRadius="md" fontSize="xs">
                  {`{
  "categories": [
    {
      "code": "HVAC",
      "name": "HVAC Systems",
      "description": "Optional description",
      "order": 1,
      "activities": [
        {
          "code": "HVAC-001",
          "name": "Activity Name",
          "defaultUnit": "No",
          "order": 1,
          "subTasks": [
            {
              "name": "Task Name",
              "defaultProductivity": 64.0,
              "unit": "No",
              "order": 1
            }
          ]
        }
      ]
    }
  ]
}`}
                </Code>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </HStack>
    </VStack>
  );
};

export default MasterDataImportExport;
